import React, { useState, useEffect, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import _ from 'lodash';
import useSaveView from '../../hooks/useSaveView';
import AutoCompleteSelectUI from '../Interface/AutoCompleteSelectUI';
import TodoCard from './TaskListTodoCard';
import { generateFilter } from '../../utils/helper';
import useView from '../../hooks/useView';
import { DebouncedInputUI, FormLabelUI } from '../Interface';
import taskListService from '../../service/taskList.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import CommentDialog from '../Comment/CommentDialog';

dayjs.extend(utc);

const TaskListTodoContent = ({ activeTab, type, tabId, autoCompleteOptions, user, getRole, autoCompleteData }) => {
  const saveView = useSaveView(user);
  const taskListTodoFilter = useView('taskListTodoFilter', user);

  const [taskListTodo, setTaskListTodo] = useState([]);
  const [roles, setRoles] = useState({});
  const [query, setQuery] = useState({
    filter: [],
    sorting: {
      sortModel: [{ field: 'startDate', sort: 'desc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 20,
        page: 0,
      },
    },
  });
  const [formVal, setFormVal] = useState({
    statusId: [],
    name: '',
    responsiblePersonId: null,
    userGroupId: null,
    lineId: null,
  });

  const [defaultFilterFields, setDefaultFilterFields] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const defCommentDialogData = {
    open: false,
    lineId: null,
    subMenuUrl: '',
  };
  const [commentDialogData, setCommentDialogData] = useState(defCommentDialogData);

  const conditions = {
    like: ['name'],
  };

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      const roles = getRole('TASKLIST_TODO_SEE_ONLY_OWN');

      setRoles(roles);
    }
  }, [user]);

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      const newFields = [
        {
          field: 'deletedBy',
          value: null,
        },
      ];
      if (type === 'own') {
        newFields.push(
          { field: 'responsiblePersonId', value: user?.userId },
          { field: 'startDate', value: dayjs().utc().startOf('day').toDate() },
          { field: 'endDate', value: dayjs().utc().endOf('day').toDate() }
        );
      } else if (type === 'group') {
        newFields.push(
          { field: 'userGroupId', value: user?.userId },
          { field: 'startDate', value: dayjs().utc().startOf('day').toDate() },
          { field: 'endDate', value: dayjs().utc().endOf('day').toDate() }
        );
      } else if (type === 'opened') {
        newFields.push({ field: 'responsiblePersonId', value: null });
        //@TODO telephely, szerep stb majd itt kell kezelni
      } else if (type === 'all' && roles.TASKLIST_TODO_SEE_ONLY_OWN) {
        newFields.push({ field: 'responsiblePersonId', value: user?.userId });
      }

      setDefaultFilterFields(newFields);
    }
  }, [activeTab, user, roles]);

  useEffect(() => {
    if (defaultFilterFields.length > 0) {
      //@TODO átnézni
      const newStatus = formVal.statusId.map((sIds) => sIds.value);
      const newFormVal = { ...formVal, statusId: newStatus };
      const newFilter = generateFilter(newFormVal, conditions);
      if (!_.isEqual(query.filter, [...defaultFilterFields, ...newFilter])) {
        setTaskListTodo([]);

        setQuery((prev) => ({
          ...prev,
          filter: [...defaultFilterFields, ...newFilter],
          pagination: {
            ...prev.pagination,
            paginationModel: { ...prev.pagination.paginationModel, page: 0 },
          },
        }));
      }
    }
  }, [formVal, defaultFilterFields]);

  useEffect(() => {
    if (Object.keys(taskListTodoFilter).length > 0) {
      setFormVal((prev) => ({
        ...prev,
        statusId: taskListTodoFilter.status || [],
      }));
    }
  }, [taskListTodoFilter]);

  const getList = useCallback(() => {
    taskListService
      .getTaskList(query)
      .then((data) => {
        if (query.pagination.paginationModel.page === 0) {
          setTaskListTodo(data.rows);
        } else {
          setTaskListTodo((prev) => [...prev, ...data.rows]);
        }
        setHasMore(taskListTodo.length + data.rows.length < data.rowCount);
      })
      .finally(() => {});
  }, [query]);

  useEffect(() => {
    if (activeTab === tabId && query.filter.length > 0) {
      getList();
    }
  }, [query, activeTab, tabId, getList]);

  const fetchMoreData = () => {
    setQuery((prev) => ({
      ...prev,
      pagination: {
        paginationModel: {
          ...prev.pagination.paginationModel,
          page: prev.pagination.paginationModel.page + 1,
        },
      },
    }));
  };

  const handleOpenCommentDialog = (lineId) => {
    setCommentDialogData({
      open: true,
      lineId,
      subMenuUrl: 'taskList',
    });
  };

  const handleCommentCountChange = (commentCount) => {
    setTaskListTodo((prev) =>
      prev.map((task) => (task.id === commentDialogData.lineId ? { ...task, commentCount } : task))
    );
  };

  return (
    <>
      <div className="grid grid-cols-5 gap-4 mx-auto">
        <div className="col-span-5 md:col-span-1">
          <DebouncedInputUI
            label={<FormLabelUI text="Keresés" />}
            debounceMs={800}
            setQuickFilterSearchValue={(newValue) => {
              setFormVal((prevState) => ({
                ...prevState,
                name: newValue,
              }));
            }}
            quickFilterSearchValue={formVal.name}
          />
        </div>
        <div className="col-span-5 md:col-span-1">
          <AutoCompleteSelectUI
            label={<FormLabelUI text="Státusz" />}
            onChange={(_event, newVal, _reason) => {
              setFormVal((prev) => ({
                ...prev,
                statusId: newVal,
              }));
              saveView('taskListTodoFilter', { status: newVal });
            }}
            selectedValue={formVal.statusId}
            disabled={false}
            freeSolo
            isOptionEqualToValue={(option, value) => option.value === value.value}
            multiple
            dataset={autoCompleteOptions.status}
          />
        </div>
        <div className={`col-span-5 md:col-span-1`}>
          <AutoCompleteSelectUI
            label={<FormLabelUI text="Projekt" />}
            onChange={(_event, newVal, _reason) => {
              setFormVal((prev) => ({
                ...prev,
                lineId: newVal?.value,
              }));
            }}
            selectedValue={formVal.lineId}
            dataset={autoCompleteOptions.project}
          />
        </div>
        <div
          className={`col-span-3 md:col-span-1 ${type === 'all' && !roles.TASKLIST_TODO_SEE_ONLY_OWN ? '' : 'hidden'}`}
        >
          <AutoCompleteSelectUI
            label={<FormLabelUI text="Felelős" />}
            onChange={(_event, newVal, _reason) => {
              setFormVal((prev) => ({
                ...prev,
                responsiblePersonId: newVal?.value,
              }));
            }}
            selectedValue={formVal.responsiblePersonId}
            dataset={autoCompleteOptions.user}
          />
        </div>
        <div
          className={`col-span-5 md:col-span-1 ${type === 'all' && !roles.TASKLIST_TODO_SEE_ONLY_OWN ? '' : 'hidden'}`}
        >
          <AutoCompleteSelectUI
            label={<FormLabelUI text="Felelősök (csoport)" />}
            onChange={(_event, newVal, _reason) => {
              setFormVal((prev) => ({
                ...prev,
                userGroupId: newVal?.value,
              }));
            }}
            selectedValue={formVal.userGroupId}
            dataset={autoCompleteOptions.userGroups}
          />
        </div>
      </div>
      <InfiniteScroll
        dataLength={taskListTodo.length}
        next={fetchMoreData}
        hasMore={hasMore}
        endMessage={
          <div className="flex justify-center">
            <p className="text-xl">Az összes feladatot megtekintette.</p>
          </div>
        }
      >
        <div className="py-10 flex flex-wrap gap-7 justify-center items-start">
          {taskListTodo.map((task, id) => {
            return (
              <TodoCard
                key={id}
                task={task}
                setTaskListTodo={setTaskListTodo}
                autoCompleteOptions={autoCompleteOptions}
                autoCompleteData={autoCompleteData}
                user={user}
                handleOpenCommentDialog={handleOpenCommentDialog}
              />
            );
          })}
        </div>
      </InfiniteScroll>
      <CommentDialog
        dialogData={commentDialogData}
        handleClose={() => setCommentDialogData(defCommentDialogData)}
        onCommentsChange={handleCommentCountChange}
      />
    </>
  );
};

export default TaskListTodoContent;
